import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import logo from "@/assets/logo_header.png";
import logoFooter from "@/assets/logo_w_footer.png";

const TermosUso = () => {
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    navigate("/");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header fixo */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b z-50 shadow-sm">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <img
            src={logo}
            alt="Elinia"
            className="cursor-pointer"
            onClick={() => {
              navigate("/");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }}
          />

          <nav className="hidden md:flex items-center gap-6 text-sm">
            <button onClick={() => {
              navigate("/sobre");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Sobre</button>
            <button onClick={() => {
              navigate("/como-funciona");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Como funciona</button>
            <button onClick={() => {
              navigate("/precos");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Preços</button>
            <button onClick={() => scrollToSection("cadastre-se")} className="hover:text-primary transition-colors">Cadastre-se</button>
            <button onClick={() => {
              navigate("/cases");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Cases</button>
            <button onClick={() => {
              navigate("/blog");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Blog</button>
            <button onClick={() => {
              navigate("/contato");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="hover:text-primary transition-colors">Contato</button>
          </nav>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/auth")}>
              Entrar
            </Button>
            <Button onClick={() => {
              navigate("/onboarding");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }} className="shadow-soft">
              Comece grátis
            </Button>
          </div>
        </div>
      </header>

      {/* Conteúdo */}
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button variant="ghost" onClick={() => navigate("/")} className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>

          <article className="prose prose-lg max-w-none space-y-8">
            <h1 className="text-4xl font-bold mb-8 text-primary">Termos de Uso</h1>

            <section>
              <h2 className="text-2xl font-bold mb-4">1. Aceitação dos Termos</h2>
              <p>Ao acessar e utilizar a plataforma Elinia, o usuário declara ter lido, entendido e concordado com os presentes Termos de Uso e com a Política de Privacidade. Caso não concorde com qualquer parte destes termos, o usuário não deve utilizar os serviços da plataforma.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Definições</h2>
              <p>Para fins deste documento:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Elinia:</strong> plataforma digital de análise e gestão de dados clínicos com uso de Inteligência Artificial;</li>
                <li><strong>Usuário:</strong> profissional de saúde, pessoa física ou jurídica, que utiliza a plataforma;</li>
                <li><strong>Paciente:</strong> indivíduo cujos dados clínicos são inseridos ou processados na plataforma;</li>
                <li><strong>Dados:</strong> informações, documentos, imagens e registros clínicos enviados pelo usuário;</li>
                <li><strong>IA (Inteligência Artificial):</strong> conjunto de algoritmos e modelos de aprendizado utilizados para gerar análises, insights e prognósticos.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Finalidade da Plataforma</h2>
              <p>A Elinia tem como objetivo auxiliar profissionais de saúde na organização, interpretação e análise de dados clínicos, permitindo uma visão mais integrada da saúde do paciente e apoiando decisões médicas.</p>
              <p className="mt-4">A plataforma não substitui o diagnóstico, julgamento clínico ou prescrição do profissional de saúde. Os resultados e insights gerados pela IA têm caráter informativo e de apoio à decisão.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Cadastro e Acesso</h2>
              <p>Para utilizar a Elinia, o usuário deve:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Realizar cadastro com informações verídicas e atualizadas;</li>
                <li>Manter suas credenciais (login e senha) em sigilo;</li>
                <li>Ser profissional de saúde devidamente habilitado ou autorizado para lidar com dados clínicos de pacientes.</li>
              </ul>
              <p className="mt-4">O usuário é responsável por todas as ações realizadas sob sua conta, incluindo o envio e tratamento de dados de pacientes.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Responsabilidades do Usuário</h2>
              <p>O usuário se compromete a:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Utilizar a plataforma de forma ética e conforme a legislação vigente (inclusive LGPD e Código de Ética Profissional);</li>
                <li>Obter o consentimento do paciente, quando necessário, antes de inserir seus dados na plataforma;</li>
                <li>Garantir a veracidade e integridade das informações enviadas;</li>
                <li>Não usar a plataforma para fins ilícitos, discriminatórios ou que possam causar dano a terceiros;</li>
                <li>Não tentar invadir, modificar, copiar ou interferir no funcionamento do sistema.</li>
              </ul>
              <p className="mt-4">A Elinia não se responsabiliza pelo conteúdo, veracidade ou legalidade dos dados inseridos pelos usuários.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Responsabilidades da Elinia</h2>
              <p>A Elinia compromete-se a:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Manter a plataforma disponível e funcional, salvo interrupções programadas ou por motivos de força maior;</li>
                <li>Proteger os dados armazenados, conforme padrões de segurança e privacidade previstos em lei;</li>
                <li>Fornecer suporte técnico e orientações sobre o uso da ferramenta;</li>
                <li>Não comercializar nem divulgar dados clínicos para terceiros sem autorização expressa.</li>
              </ul>
              <p className="mt-4">Contudo, a Elinia não garante resultados clínicos, diagnósticos ou interpretações automáticas livres de erro — a decisão final cabe exclusivamente ao profissional de saúde.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Propriedade Intelectual</h2>
              <p>Todos os elementos da plataforma — incluindo marca, design, interface, algoritmos e modelos de IA — são propriedade exclusiva da Elinia e protegidos por leis de direitos autorais e propriedade industrial.</p>
              <p className="mt-4">É proibida a reprodução, modificação ou distribuição de qualquer parte da plataforma sem autorização expressa.</p>
              <p className="mt-4">Os dados enviados pelos usuários continuam sendo de propriedade dos respectivos profissionais e pacientes, sendo utilizados apenas conforme descrito na Política de Privacidade.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Licença de Uso</h2>
              <p>A Elinia concede ao usuário uma licença limitada, não exclusiva e intransferível para uso da plataforma conforme estes Termos. Esta licença pode ser revogada a qualquer momento em caso de violação das condições aqui descritas.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Limitação de Responsabilidade</h2>
              <p>A Elinia não se responsabiliza por:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Decisões clínicas tomadas com base em análises geradas pela IA;</li>
                <li>Dano moral, material ou financeiro decorrente de uso inadequado da plataforma;</li>
                <li>Falhas de internet, sistemas de terceiros ou eventos fora do controle da empresa;</li>
                <li>Conteúdos ou dados inseridos incorretamente pelos usuários.</li>
              </ul>
              <p className="mt-4">O uso da Elinia é de apoio e não de substituição à atuação humana do profissional de saúde.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Cancelamento e Encerramento de Conta</h2>
              <p>O usuário pode solicitar o encerramento da conta a qualquer momento. A Elinia reserva-se o direito de suspender ou excluir contas que:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Violem estes Termos;</li>
                <li>Causem riscos à segurança do sistema;</li>
                <li>Utilizem a plataforma de forma ilegal ou inadequada.</li>
              </ul>
              <p className="mt-4">Após o encerramento, os dados poderão ser anonimizados ou excluídos, conforme a Política de Privacidade.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Atualizações e Modificações</h2>
              <p>A Elinia poderá alterar estes Termos de Uso a qualquer momento, mediante publicação da versão atualizada em seu site. O uso contínuo da plataforma após tais alterações implica concordância com as novas condições.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">12. Legislação Aplicável e Foro</h2>
              <p>Esta Política é regida pela legislação brasileira, especialmente pela Lei Geral de Proteção de Dados (LGPD). Fica eleito o foro da comarca de Caxias do Sul – RS para dirimir eventuais controvérsias decorrentes destes Termos, com renúncia a qualquer outro, por mais privilegiado que seja.</p>
            </section>
          </article>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-primary via-primary/95 to-secondary text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <img src={logoFooter} alt="Elinia" className="mb-4" />
              <p className="text-sm opacity-90 leading-relaxed">
                Inteligência que facilita a vida de quem cuida.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-base">Navegação</h4>
              <div className="space-y-2 text-sm">
                <button onClick={() => { navigate("/sobre"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Sobre</button>
                <button onClick={() => { navigate("/como-funciona"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Como funciona</button>
                <button onClick={() => { navigate("/precos"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Preços</button>
                <button onClick={() => {
                  navigate("/onboarding");
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }} className="block hover:translate-x-1 transition-transform">→ Cadastre-se</button>
                <button onClick={() => { navigate("/cases"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Cases</button>
                <button onClick={() => { navigate("/blog"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Blog</button>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-base">Legal</h4>
              <div className="space-y-2 text-sm">
                <button onClick={() => { navigate("/politica-privacidade"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Política de Privacidade</button>
                <button onClick={() => { navigate("/termos-uso"); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="block hover:translate-x-1 transition-transform">→ Termos de Uso</button>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-base">Contato</h4>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2 opacity-90">
                  <Mail className="h-4 w-4" />
                  contato@miasalute.com.br
                </p>
                <button
                  onClick={() => {
                    navigate("/contato");
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-all hover:scale-105"
                >
                  Fale conosco
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 text-center text-sm opacity-90">
            <p>&copy; 2026 Elinia. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermosUso;
